/**
 * React Hook for Entity Registry with Context Integration
 * 
 * Provides a hook that creates entity adapters with injected API client from context.
 * This follows the dependency injection pattern and SOLID principles.
 */

import { useMemo } from 'react';
import { useApiClient } from '@/lib/api/client';
import { EntityRegistry, EntityAdapter } from '../registry/entities/entityRegistry';
import { registerEntities } from '../registry/entities/entities.registered';

/**
 * Hook that provides entity registry with context-aware adapters
 * @returns Object with registry and adapter creation methods
 */
export function useEntityRegistry() {
  const { apiRequest } = useApiClient();
  
  // Create registry instance and register entities (memoized)
  const registry = useMemo(() => {
    const reg = new EntityRegistry();
    registerEntities(reg);
    return reg;
  }, []);

  // Create API client wrapper for dependency injection
  const apiClient = useMemo(() => ({
    apiRequest
  }), [apiRequest]);

  // Memoized adapter creation methods
  const adapters = useMemo(() => ({
    /**
     * Get adapter for specific entity type
     * @param entityType - Entity type identifier
     * @returns EntityAdapter instance or undefined
     */
    getAdapter: (entityType: string): EntityAdapter | undefined => {
      return registry.createAdapter(entityType, apiClient);
    },

    /**
     * Get all adapters for registered entities
     * @returns Map of entity type to adapter instance
     */
    getAllAdapters: (): Map<string, EntityAdapter> => {
      return registry.createAllAdapters(apiClient);
    }
  }), [registry, apiClient]);

  return {
    registry,
    ...adapters
  };
}

/**
 * Hook for getting a specific entity adapter
 * @param entityType - Entity type identifier
 * @returns EntityAdapter instance or undefined
 */
export function useEntityAdapter(entityType: string): EntityAdapter | undefined {
  const { getAdapter } = useEntityRegistry();
  return useMemo(() => getAdapter(entityType), [getAdapter, entityType]);
}